import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import Reward from 'react-rewards';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {Flex} from 'reflexbox';
import { useNavigate } from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux';
import DialogPopUp from '../common/AlertDialog';
import {CheckCircle, Clear} from '@mui/icons-material';
import CardContainer from './CardContainer';
import StuckTimerDialog from '../gamePlayComponents/StuckTimerDialog';
import PropTypes from 'prop-types';
import {hintUsedPunishment, wrongAnswerPunishment} from '../../helpers/pointScoring';
import Helmet from "react-helmet";
import * as TYPES from "../courseContent/questionTypes";
import MixAndMatch from "./MixAndMatch";
import FreeText from "./FreeText";
import MultipleChoice from "./MultipleChoice";
import {writeTeamProgress, writeTeamQuestionHistory} from "../../helpers/dbFunctions";
import { createAlert } from "../../redux/uiSlice";
import {messageType} from "../common/RequestAlertBar";
import { useTheme } from '@mui/material/styles';


const QuestionParent = (props) => {
  let navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { uid } = useSelector((state) => state.user);
  const { currentTeamId, teamDetails } = useSelector((state) => state.team);
  const courseName = teamDetails?.currentCourseSelection || '';
  const questionState = teamDetails?.questions?.[props.questionKey] || {};
  const teammateInfo = useMemo(() => teamDetails?.teammateInfo || {}, [teamDetails?.teammateInfo]);

  const totalPossiblePoints = props.totalPossiblePoints ?? 100;
  //local state that will be updated from server in useEffects
  const [userActions, setUserActions] = useState([]);
  const [pastAnswers, setPastAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [availablePoints, setAvailablePoints] = useState(totalPossiblePoints);
  //local values.
  const [userAnswer, setUserAnswer] = useState('');
  const [hintDialog, setHintDialog] = useState(false);
  const [skipDialog, setSkipDialog] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [goToNextFlag, setGoToNextFlag] = useState(false);
  const [error, setError] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(true);

  const rewardRef = useRef(null);

  const hintUsed = userActions.includes('hint');
  const correct = userActions.includes('success');
  const complete = userActions.includes('skip') || correct;

  //useEffects to set component state after the redux store has loaded.
  useEffect(() => {
    if (questionState?.userActions?.length > userActions.length ) {
      const newUserAction = questionState.userActions?.[questionState.userActions.length - 1];
      const mostRecentUserSubmission = questionState.pastAnswers?.[questionState.userActions.length - 1];
      const submittedByUid = questionState.lastSubmittedBy;
      const isFromTeammate = submittedByUid && submittedByUid !== uid;

      // When a teammate takes an action, show who did what
      if (isFromTeammate) {
        const teammate = teammateInfo[submittedByUid];
        const name = teammate?.name || 'A teammate';

        if (newUserAction === 'incorrect' || newUserAction === 'success') {
          const suffix = newUserAction === 'incorrect' ? ' (incorrect)' : '';
          dispatch(createAlert({
            type: newUserAction === 'incorrect' ? messageType.error : messageType.info,
            message: `${name} answered ${mostRecentUserSubmission ?? ''}${suffix}`,
            submittedBy: teammate ? { name, character: teammate.character, color: teammate.color } : null,
            submittedAnswer: mostRecentUserSubmission,
            teammateAction: `answered ${mostRecentUserSubmission ?? ''}${suffix}`,
          }));
        }

        if (newUserAction === 'hint') {
          dispatch(createAlert({
            type: messageType.success,
            message: `${name} used a hint`,
            submittedBy: teammate ? { name, character: teammate.character, color: teammate.color } : null,
            teammateAction: 'used a hint',
          }));
        }

        if (newUserAction === 'skip') {
          dispatch(createAlert({
            type: messageType.warning,
            message: `${name} skipped the question`,
            submittedBy: teammate ? { name, character: teammate.character, color: teammate.color } : null,
            teammateAction: 'skipped the question',
          }));
        }
      }

      newUserAction==='incorrect' && !isFromTeammate && dispatch(createAlert({
        type: messageType.error,
        message: `"${mostRecentUserSubmission}" is incorrect`,
      }));
      newUserAction==='hint' && !isFromTeammate && dispatch(createAlert({
        type: messageType.success,
        message: "Hint used!",
      }));
      newUserAction==='skip' && !isFromTeammate && dispatch(createAlert({
        type: messageType.warning,
        message: "Question skipped!",
      }));
      newUserAction==='success' && (setSuccessDialog(true))
    }
    setUserActions(questionState.userActions || []);
  }, [questionState.userActions, questionState.lastSubmittedBy, questionState.pastAnswers, teammateInfo, uid, dispatch]);
  useEffect(() => {
    setPastAnswers(questionState.pastAnswers || []);
  }, [questionState.pastAnswers]);
  useEffect(() => {
    const serverAvailablePoints = questionState.availablePoints;
    const serverPointsScored = questionState.pointsScored;

    if (typeof serverAvailablePoints === 'number') {
      setAvailablePoints(serverAvailablePoints);
    } else if (typeof serverPointsScored === 'number') {
      setAvailablePoints(serverPointsScored);
    } else {
      setAvailablePoints(totalPossiblePoints);
    }
  }, [questionState.availablePoints, questionState.pointsScored, totalPossiblePoints]);
  useEffect(() => {
    setUserAnswer(questionState.userAnswer || '');
  }, [questionState.userAnswer]);
  useEffect(()=> {
    if(questionState.startTime) {
      setStartTime(questionState.startTime);
    } else {
      const currentTime = Math.round(new Date().getTime() / 1000);
      writeTeamQuestionHistory(currentTeamId, props.questionKey, {startTime: currentTime});
    }
  },[questionState.startTime]);

  //logic: if user actions for this question don't have skip or complete, then this is the furthest question
  useEffect(() => {
    //only dispatch if its a new question; don't dispatch if viewing a previously answered question
    //will need to change the props.nextURL bit to be consistent
    !complete &&  writeTeamProgress(currentTeamId, courseName, props.pageURL)
  }, []);

  useEffect(() => {
    if (successDialog) {
      setTimeout(() => rewardRef.current?.rewardMe(), 300);
    }
  });

  const handleHint = () => {
    const dbPayload = {
      lastSubmittedBy: uid,
      startTime: startTime,
      availablePoints: Math.max(availablePoints - hintUsedPunishment, 0),
      userActions: [...userActions, 'hint'],
    };
    writeTeamQuestionHistory(currentTeamId, props.questionKey, dbPayload);
  };

  const checkAnswer = useCallback((answer, userAnswer) => {
    if (props.type === TYPES.freeTextQuestion) {
      return (answer.test(userAnswer.toLowerCase().trim()) || userAnswer === answer);
    } else { //i.e. if(props.type === TYPES.mixAndMatch)
      return (answer.toString().toLowerCase() === userAnswer.toString().toLowerCase())
    }
  }, [props.type]);

  const handleAnswerSubmit = useCallback(() => {
    //setPastAnswers([...pastAnswers, userAnswer]);
    const dbPayload = { lastSubmittedBy: uid };
    dbPayload.startTime = startTime;
    dbPayload.pastAnswers = [...pastAnswers, userAnswer];
    if (checkAnswer(props.answer.answer, userAnswer)) {
      //correct answer
      dbPayload.userActions = [...userActions, 'success'];
      dbPayload.endTime = Math.round(new Date().getTime() / 1000);
      dbPayload.pointsScored = availablePoints;
      writeTeamQuestionHistory(currentTeamId, props.questionKey, dbPayload);
      setSuccessDialog(true);
    } else {
      //try again
      dbPayload.userActions = [...userActions, 'incorrect'];
      dbPayload.availablePoints = Math.max(availablePoints - wrongAnswerPunishment, 0);
      writeTeamQuestionHistory(currentTeamId, props.questionKey, dbPayload);
      setError(true);
    }
    //insert db call to update team
  }, [uid, pastAnswers, userAnswer, startTime, availablePoints, userActions, currentTeamId, props.questionKey, props.answer.answer, checkAnswer]);

  const handleSkip = () => {
    const dbPayload = {
      lastSubmittedBy: uid,
      availablePoints: 0,
      pointsScored: 0,
      endTime: Math.round(new Date().getTime() / 1000),
      userActions: [...userActions, 'skip']
    };
    writeTeamQuestionHistory(currentTeamId, props.questionKey, dbPayload);
    setGoToNextFlag(true);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Enter') return;

      // While answering: Enter submits (if enabled). After completion: Enter continues.
      if (!complete && !disableSubmit) {
        event.preventDefault();
        handleAnswerSubmit();
      } else if (complete) {
        event.preventDefault();
        setGoToNextFlag(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [complete, disableSubmit, handleAnswerSubmit]);

  const goToNext = useCallback(() => {
    navigate(props.nextURL);
  }, [navigate, props.nextURL]);

  useEffect(() => {
    if (goToNextFlag) {
      goToNext();
    }
  }, [goToNextFlag, goToNext]);

  //totalPossiblePoints
  const subheader = (
    <Flex>
      <Typography style={{marginRight: '4px'}}>{complete ? 'Points scored:' : 'Available Points:'} </Typography>
      <Typography
        style={{
          textDecoration:
            availablePoints !== totalPossiblePoints && 'line-through',
          marginRight: '4px'
        }}
      >
        {' '}
        {totalPossiblePoints}{' '}
      </Typography>
      <Typography color={'secondary'}>
        {' '}
        {availablePoints !== totalPossiblePoints && availablePoints}{' '}
      </Typography>
    </Flex>
  );
  const boldQuestionHintAndInput = (<>
    <Flex style={{flexDirection: 'column'}}>
      <Typography
        component={'span'}
        style={{fontWeight: 'bold', marginBottom: '8px'}}
      >
        {props.boldContent}{' '}
      </Typography>
      {props.captionContent && <Typography variant={'subtitle2'}> {props.captionContent} </Typography>}
      {hintUsed && (
        <Typography color={'primary'} style={{marginTop: '8px'}}>
          Hint: {props.hint}{' '}
        </Typography>
      )}
    </Flex>
    {props.type === TYPES.freeTextQuestion && <FreeText setUserAnswer={(el) => setUserAnswer(el)}
                                                        userAnswer={userAnswer}
                                                        userActions={userActions}
                                                        pastAnswers={pastAnswers}
                                                        error={error}
                                                        setError={(el) => setError(el)}
                                                        setDisableSubmit={(el) => setDisableSubmit(el)}
                                                        disableSubmit={disableSubmit}
                                                        complete={complete}
                                                        correct={correct}
    />}

    {props.type === TYPES.mixAndMatch && <MixAndMatch setUserAnswer={(el) => setUserAnswer(el)}
                                                      error={error}
                                                      setError={(el) => setError(el)}
                                                      complete={complete}
                                                      userAnswer={userAnswer}
                                                      userActions={userActions}
                                                      pastAnswers={pastAnswers}
                                                      correct={correct}
                                                      setDisableSubmit={(el) => setDisableSubmit(el)}
                                                      initialCards={props.specialContent.initialCards}
                                                      initialImages={props.specialContent.initialImages}/>}
    {props.type === TYPES.multipleChoice && <MultipleChoice setUserAnswer={(el) => setUserAnswer(el)}
                                                            error={error}
                                                            setError={(el) => setError(el)}
                                                            complete={complete}
                                                            userAnswer={userAnswer}
                                                            userActions={userActions}
                                                            pastAnswers={pastAnswers}
                                                            correct={correct}
                                                            setDisableSubmit={(el) => setDisableSubmit(el)}
                                                            initialCards={props.specialContent?.initialCards}
                                                            initialImages={props.specialContent?.initialImages}/>}
  </>);

  const actionsComponent = (<Flex id={'buttons'} style={{justifyContent: 'space-around'}}>
    <Button
      color="primary"
      disabled={hintUsed || !props.hint}
      onClick={() => setHintDialog(true)}
    >
      Hint
    </Button>
    <Button
      variant="contained"
      color="primary"
      disabled={disableSubmit}
      onClick={handleAnswerSubmit}
      style={{width: '40%'}}
    >
      Submit
    </Button>
    <Button color="primary" onClick={() => setSkipDialog(true)}>
      Skip
    </Button>
  </Flex>);
  const completeComponent = (<Flex w={1} justifyContent={'space-between'}>
    <Flex flex={1}/>
    <Button
      variant="contained"
      color="primary"
      disabled
      style={{width: '40%', flex: 1}}
    >
      {correct ? (
        <CheckCircle style={{fontSize: 30}} color={'primary'}/>
      ) : (
        <Clear style={{fontSize: 30}} color={'secondary'}/>
      )}
    </Button>
    <Button
      color="primary"
      style={{flex: 1}}
      onClick={() => setGoToNextFlag(true)}
    >
      Continue
    </Button>
  </Flex>);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
      </Helmet>
      <CardContainer
        location={props.location}

        cardMediaImg={props.image1}
        cardMediaImg1Caption={props.image1Caption || null}
        cardContent1={props.content1 || null}

        cardMediaImg2={props.image2 || null}
        cardMediaImg2Caption={props.image2Caption || null}
        cardContent2={props.content2 || null}

        cardContent3={boldQuestionHintAndInput}

        actionsComponent={complete ? completeComponent : actionsComponent}
        subheader={subheader}
        additionalTitle={props.nOfNQuestions}
      />
      <StuckTimerDialog wrongAnswer={error} complete={complete}/>
      <DialogPopUp open={hintDialog} close={() => setHintDialog(false)} continue={handleHint}
                   title={'Use Hint?'}
                   body={'Available points for the question will decrease by ' + hintUsedPunishment + '.'}
      />
      <DialogPopUp open={skipDialog} close={() => setSkipDialog(false)} continue={handleSkip}
                   title={'Skip question?'}
                   body={'You won\'t score any points for this question.'}
      />
      <DialogPopUp
        open={successDialog}
        close={() => setSuccessDialog(false)}
        continue={() => setGoToNextFlag(true)}
        title={null}
        titleStyle={{textAlign: 'center', padding: 0, width: '300px'}}
        disableCancel={true}
        noScrollBody
        body={
          <Flex w={1}
                style={{justifyContent: 'center', alignItems: 'center', flexDirection: 'column', textAlign: 'center'}}>
            <Typography variant={'h6'}
                        style={{textAlign: 'center', padding: '16px', width: '100%'}}> Correct! </Typography>
            <CheckCircle style={{fontSize: 90, zIndex: 4}} color={'primary'}
                         onClick={() => rewardRef.current?.rewardMe()}/>
            <div style={{width: '1px', height: '1px', backgroundColor: 'blue', position: 'relative', bottom: '58px'}}>
              <Reward ref={rewardRef} type='memphis' config={{ containerStyle: { pointerEvents: 'none' } }}>
                <div onClick={() => rewardRef.current?.rewardMe()}/>
              </Reward>
            </div>
            <div style={{height: '0px'}}>
              <div id={'circle'} style={{
                width: '74px',
                height: '74px',
                backgroundColor: theme.palette.background.paper,
                position: 'relative',
                bottom: '83px',
                borderRadius: '50%'
              }}/>
            </div>
            <Typography style={{marginTop: '8px'}}> You earned <span
              style={{fontWeight: 'bold'}}>{availablePoints}</span> points. {availablePoints === 0 && " But you're amazing for your perseverance!"}
            </Typography>

          </Flex>
        }
      />
    </>
  );
};
QuestionParent.propTypes = {
  location: PropTypes.object,
  pageURL: PropTypes.any.isRequired,
  answer: PropTypes.any.isRequired,
  additionalTitle: PropTypes.any,
  nextURL: PropTypes.any.isRequired,
  image1: PropTypes.string,
  image1Caption: PropTypes.string,
  content: PropTypes.any,
  boldContent: PropTypes.any.isRequired,
  nOfNQuestions: PropTypes.any,
  questionKey: PropTypes.string.isRequired,
  totalPossiblePoints: PropTypes.number,
};

export default QuestionParent;
